import { SchemaTypes } from '@allesauto/graphql';
import translate from '@allesauto/i18n';
import clsx from 'clsx';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { WL_INTEGRATION_SLUG_AUTO_HEMPEL } from 'web/src/features/whitelabel/util/getWhiteLabelIntegrationSlugs';
import { saveAccessoryPcpPagination } from 'web/src/store/redux/slices/pages/accessoryPcp';

import { DropdownItemType } from '../dropdown';
import SearchDropdown, { Option } from '../searchDropdown';
import { useWhiteLabel } from '../whiteLabel';

export interface MakeModelVariantProps {
  id: string;
  index: number;
  makeItems: DropdownItemType[];
  modelItems?: DropdownItemType[];
  variantItems?: DropdownItemType[];
  selectedModel?: string;
  selectedMake?: string;
  selectedVariant?: string;
  makeLabel: string;
  modelLabel: string;
  variantLabel?: string;
  enableVariant?: boolean;
  onChange?: (
    values: {
      make: string | number | undefined;
      model: string | number | undefined;
      variant: string | number | undefined;
    },
    index: number
  ) => void;
  onMakeChange?: (make: string | number | undefined, index: number) => void;
  onModelChange?: (model: string | number | undefined, index: number) => void;
  onVariantChange?: (
    variant: string | number | undefined,
    index: number
  ) => void;
  isPcpAndHomepageModelDropdown?: boolean;
  modelDropdownItemsPcpAndHomepage?: SchemaTypes.Model[];
}

export interface MakeModelVariantFilterChangeProps {
  value: string | number;
  index: number;
}

const MakeModelVariant: FC<MakeModelVariantProps> = (props) => {
  const {
    index = 0,
    modelItems,
    makeItems,
    variantItems,
    selectedMake,
    selectedModel,
    selectedVariant,
    makeLabel,
    modelLabel,
    variantLabel,
    enableVariant = false,
    onChange,
    onMakeChange,
    onModelChange,
    onVariantChange,
    isPcpAndHomepageModelDropdown = false,
    modelDropdownItemsPcpAndHomepage,
  } = props;
  const intl = useIntl();
  const dispatch = useDispatch();
  const whiteLabel = useWhiteLabel();
  const isAutoHempelHeader =
    whiteLabel?.merchantOrigin === WL_INTEGRATION_SLUG_AUTO_HEMPEL;
  const [isModelDisabled, setIsModelDisabled] = useState(
    !(modelItems && modelItems.length > 0)
  );
  const [isVariantDisabled, setIsVariantDisabled] = useState(
    !(variantItems && variantItems.length > 0)
  );

  const [
    modelDropdownItemsPcpAndHomepageDisabled,
    setModelDropdownItemsPcpAndHomepageDisabled,
  ] = useState(
    !(
      modelDropdownItemsPcpAndHomepage &&
      modelDropdownItemsPcpAndHomepage.length > 0
    )
  );
  const trackingIdentifier = 'aa-filter';
  const trackingIdentifierMake = `${trackingIdentifier}--make--id${index}`;
  const trackingIdentifierModel = `${trackingIdentifier}--model--id${index}`;
  const trackingIdentifierVariant = `${trackingIdentifier}--variant--id${index}`;

  const findDisplayName = (
    models: SchemaTypes.Model[] | undefined,
    name: string
  ) => {
    const model = models?.find((m) => m.name === name);
    return model ? model.displayName : selectedModel;
  };

  const handleDropdownChange = useCallback(
    (
      option: Option | readonly Option[] | null,
      type: 'make' | 'model' | 'variant'
    ) => {
      const selectedOption = Array.isArray(option) ? option[0] : option;
      const value = selectedOption ? selectedOption.value : null;

      if (type === 'make') {
        onChange?.(
          {
            make: value,
            model: undefined,
            variant: undefined,
          },
          index
        );

        onMakeChange?.(value, index);
        setIsModelDisabled(false);
        setModelDropdownItemsPcpAndHomepageDisabled(false);

        if (!option) {
          onVariantChange?.(undefined, index);

          setIsModelDisabled(true);
          setModelDropdownItemsPcpAndHomepageDisabled(true);
          setIsVariantDisabled(true);
        }
      }

      if (type === 'model') {
        onChange?.(
          {
            make: selectedMake,
            model: value,
            variant: undefined,
          },
          index
        );

        onModelChange?.(value, index);

        setIsVariantDisabled(false);

        if (!option) {
          onVariantChange?.(undefined, index);

          setIsVariantDisabled(true);
        }
      }

      if (type === 'variant') {
        onChange?.(
          {
            make: selectedMake,
            model: selectedModel,
            variant: value,
          },
          index
        );

        onModelChange?.(value, index);
      }

      dispatch(saveAccessoryPcpPagination(1));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    [
      dispatch,
      index,
      onChange,
      onMakeChange,
      onModelChange,
      onVariantChange,
      selectedMake,
      selectedModel,
    ]
  );

  useEffect(() => {
    setIsModelDisabled(!(modelItems && modelItems.length > 0));
  }, [modelItems]);

  useEffect(() => {
    setModelDropdownItemsPcpAndHomepageDisabled(
      !(
        modelDropdownItemsPcpAndHomepage &&
        modelDropdownItemsPcpAndHomepage.length > 0
      )
    );
  }, [modelDropdownItemsPcpAndHomepage]);

  useEffect(() => {
    setIsVariantDisabled(!(variantItems && variantItems.length > 0));
  }, [variantItems]);

  const dropdownModelOptions =
    (modelDropdownItemsPcpAndHomepage?.map((m) => {
      if (m.subGroups?.length) {
        const subGroupOptions = m.subGroups.map((subGroup) => ({
          value: String(subGroup?.name),
          label: String(subGroup?.displayName),
        })) as Option[];

        // Add (Alle) if subGroups exist
        subGroupOptions.unshift({
          value: String(m?.name),
          label: String(m?.displayName) + ' (Alle)',
        });

        return {
          label: (
            <label className="font-weight-bold text-[#a09c9c] text-[16px]">
              {String(m?.displayName)}
            </label>
          ),
          options: subGroupOptions,
        };
      } else {
        return {
          value: String(m?.name),
          label: String(m?.displayName),
        };
      }
    }) as Option[]) || [];

  return (
    <>
      <div className="pb-20px" data-tracking-id={trackingIdentifierMake}>
        <label className="text-white">{makeLabel}</label>
        <div className="mt-10px">
          <SearchDropdown
            id={trackingIdentifierMake}
            placeholder={translate(intl, 'ui.PCP.FILTERS.FILTER_PLACEHOLDER')}
            options={makeItems.map((dropdownItem) => ({
              value: dropdownItem.value,
              text: dropdownItem.text,
              label: dropdownItem.text,
            }))}
            isMulti={false}
            darkBg={true}
            value={
              selectedMake ? { value: selectedMake, label: selectedMake } : null
            }
            onChange={(option) => handleDropdownChange(option, 'make')}
            context="relative-position"
          />
        </div>
      </div>
      <div className="pb-20px" data-tracking-id={trackingIdentifierModel}>
        <label className="mb-10px text-white">{modelLabel}</label>
        <div className={clsx('mt-10px', isAutoHempelHeader && 'autohempel')}>
          {isPcpAndHomepageModelDropdown ? (
            <SearchDropdown
              id={trackingIdentifierModel}
              placeholder={translate(intl, 'ui.PCP.FILTERS.FILTER_PLACEHOLDER')}
              options={dropdownModelOptions}
              isMulti={false}
              darkBg={true}
              value={
                selectedModel
                  ? {
                      value: selectedModel,
                      label:
                        findDisplayName(
                          modelDropdownItemsPcpAndHomepage,
                          selectedModel
                        ) ?? '',
                    }
                  : null
              }
              disabled={modelDropdownItemsPcpAndHomepageDisabled}
              onChange={(option) => {
                handleDropdownChange(option, 'model');
              }}
              context="relative-position"
            />
          ) : (
            <SearchDropdown
              id={trackingIdentifierModel}
              placeholder={translate(intl, 'ui.PCP.FILTERS.FILTER_PLACEHOLDER')}
              options={
                modelItems?.map((dropdownItem) => ({
                  value: dropdownItem.value,
                  text: dropdownItem.text,
                  label: dropdownItem.text,
                })) || []
              }
              isMulti={false}
              darkBg={true}
              value={
                selectedModel
                  ? { value: selectedModel, label: selectedModel }
                  : null
              }
              disabled={isModelDisabled}
              onChange={(option) => handleDropdownChange(option, 'model')}
              context="relative-position"
            />
          )}
        </div>
      </div>
      {enableVariant && (
        <div className="pb-20px" data-tracking-id={trackingIdentifierVariant}>
          <label className="mb-10px text-white">{variantLabel}</label>
          <div className="mt-10px">
            <SearchDropdown
              id={trackingIdentifierVariant}
              placeholder={translate(intl, 'ui.PCP.FILTERS.FILTER_PLACEHOLDER')}
              options={
                variantItems?.map((dropdownItem) => ({
                  value: dropdownItem.value,
                  text: dropdownItem.text,
                  label: dropdownItem.text,
                })) || []
              }
              isMulti={false}
              darkBg={true}
              value={
                selectedVariant
                  ? { value: selectedVariant, label: selectedVariant }
                  : null
              }
              disabled={isVariantDisabled}
              onChange={(option) => handleDropdownChange(option, 'variant')}
              context="relative-position"
            />
          </div>
        </div>
      )}
    </>
  );
};

export default MakeModelVariant;
